import { Dispatch, FC, ReactNode, createContext, useContext, useReducer, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useParams } from "react-router-dom";
import { NotFound } from "@app/shared/ErrorPages";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { ResourceType } from "@app/resources";
import { UserContext } from "@app/user/UserContext";
import { MultiMapExportAdequacyCombination, MultiMapExportRouteMatchParams, MultiMapExportRouteState } from "@app/projects/network-assessment/multi-map-export/interfaces";
import { MultiMapExportActions, MultiMapExportState, multiMapExportInitialState, multiMapExportReducer } from "@app/projects/network-assessment/multi-map-export/state-management/multiMapExportStateManagement";
import { useMultiMapExportAdequacyOutcomes } from "@app/projects/network-assessment/multi-map-export/hooks/useMultiMapExportAdequacyOutcomes";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import Features from "@app/feature-flags/features";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Project from "@app/projects/project";
import ProjectService from "@app/projects/projectService";

export interface MultiMapExportContextProps {
  /** Every analysed county/specialty combination and its outcome; the right rail counts against these. */
  adequacyCombinations?: readonly MultiMapExportAdequacyCombination[];
  adequacyCombinationsLoading?: boolean;
  launchContext?: MultiMapExportRouteState;
  project?: Project;
  projectRunConfiguration?: ProjectRunConfiguration;
  projectRunConfigurationId?: string;
  selections?: MultiMapExportState;
  selectionsChanged?: Dispatch<MultiMapExportActions>;
}

export const MultiMapExportContext = createContext<Partial<MultiMapExportContextProps>>({});

export interface MultiMapExportContextProviderProps {
  children?: ReactNode;
}

export const MultiMapExportContextProvider: FC<MultiMapExportContextProviderProps> = ({ children }: MultiMapExportContextProviderProps) => {
  const { projectId, projectRunConfigurationId } = useParams<MultiMapExportRouteMatchParams>();
  const launchContext = useLocation<MultiMapExportRouteState>().state;
  const { enabledFeatureFlags, featureFlagsLoading } = useContext(FeatureFlagContext);
  const user = useContext(UserContext);
  const projectService = useRef(new ProjectService()).current;
  const multiMapExportEnabled = enabledFeatureFlags.has(Features.VOLUME_MAP_EXPORTS_COUNTY);
  const [selections, selectionsChanged] = useReducer(multiMapExportReducer, multiMapExportInitialState(launchContext));

  const { data: project, error: projectError } = useQuery<Project, JQuery.jqXHR>({
    enabled: multiMapExportEnabled,
    queryFn: ({ signal }) => projectService.getById(Number(projectId), true, false, undefined, false, signal),
    queryKey: ["project", user?.userId, projectId],
    staleTime: 5 * 60 * 1000,
  });

  const { combinations, error: adequacyOutcomesError, isLoading, isSuccess } = useMultiMapExportAdequacyOutcomes(project?.projectId, Number(projectRunConfigurationId));
  const requestError = projectError ?? adequacyOutcomesError;

  if (requestError) {
    throw { status: requestError.status, statusText: requestError.statusText, traceId: requestError.responseJSON?.traceId };
  }

  if (featureFlagsLoading) {
    return <LoadingSpinner />;
  }

  if (!multiMapExportEnabled) {
    return <NotFound />;
  }

  if (!project) {
    return <LoadingSpinner />;
  }

  const projectRunConfiguration = project.projectRunConfigurations
    ?.find((runConfiguration) => runConfiguration.projectRunConfigurationId === Number(projectRunConfigurationId));

  const isCountyAdequacyRun = project.resource.resourceType === ResourceType.CustomProject
    && !!projectRunConfiguration
    && !!projectRunConfiguration.adequacyTemplateId
    && !projectRunConfiguration.accuracyTemplateId;

  if (!isCountyAdequacyRun) {
    return <NotFound />;
  }

  return (
    <MultiMapExportContext.Provider
      value={{
        adequacyCombinations: combinations,
        adequacyCombinationsLoading: isLoading || !isSuccess,
        launchContext,
        project,
        projectRunConfiguration,
        projectRunConfigurationId,
        selections,
        selectionsChanged,
      }}
    >
      {children}
    </MultiMapExportContext.Provider>
  );
};
